import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';
import { AwardEvent } from './awards';

export type LeaderboardRow = {
  studentId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  formId: string | null;
  formName: string | null;
  yearGroupId: string | null;
  yearGroupName: string | null;
  houseId: string | null;
  houseName: string | null;
  totalPoints: number;
  awardCount: number;
  avatar?: string | null;
};

export type HouseBoard = {
  houseId: string;
  houseName: string;
  items: LeaderboardRow[];
  count: number;
};

export function getHousePointsLeaderboard(
  user: AppUser,
  uiFilter?: { where?: any; limit?: number; skip?: number }
): Promise<LeaderboardRow[]> {
  const { roleSessionKey: usid, activeSchoolId } = user;

  const filter: any = {
    limit: uiFilter?.limit ?? DEFAULT_LIMIT,
    skip: uiFilter?.skip ?? DEFAULT_SKIP
  };
  if (uiFilter?.where) filter.where = uiFilter.where;

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/leaderboards/housePoints`, {
      headers: { usid },
      params: { filter: JSON.stringify(filter) }
    })
    .then(r => r.data);
}

/** ===== Count endpoint (separate), same as your other *Count helpers ===== */
export function getHousePointsLeaderboardCount(user: AppUser, where?: any): Promise<{ count: number }> {
  const { roleSessionKey: usid, activeSchoolId } = user;
  const filterObj = where ? { where } : {};

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/leaderboards/housePoints/count`, {
      headers: { usid },
      params: { filter: JSON.stringify(filterObj) }
    })
    .then(r => r.data);
}

/** ===== Convenience wrappers (optional, same pattern you use elsewhere) ===== */

// Global leaderboard (no house filter)
export function getGlobalHousePointsLeaderboard(
  user: AppUser,
  uiFilter?: { where?: any; limit?: number; skip?: number }
) {
  const where = { ...(uiFilter?.where || {}) }; // no houseId
  return getHousePointsLeaderboard(user, { ...uiFilter, where });
}

export function getGlobalHousePointsLeaderboardCount(user: AppUser, search?: string) {
  const where = search ? { search } : undefined;
  return getHousePointsLeaderboardCount(user, where);
}

// Per-house leaderboard
export function getHouseOnlyPointsLeaderboard(
  user: AppUser,
  houseId: string,
  uiFilter?: { where?: any; limit?: number; skip?: number }
) {
  const where = { ...(uiFilter?.where || {}), houseId };
  return getHousePointsLeaderboard(user, { ...uiFilter, where });
}

export function getHouseOnlyPointsLeaderboardCount(user: AppUser, houseId: string, search?: string) {
  const where: any = { houseId };
  if (search) where.search = search;
  return getHousePointsLeaderboardCount(user, where);
}

type StudentAwardsOpts = { houseId?: string; academicYearId?: string } | { where?: any } | undefined;

export function getStudentAwardsForAcademicYear(
  user: AppUser,
  studentId: string,
  uiFilter?: StudentAwardsOpts
): Promise<AwardEvent[]> {
  const { roleSessionKey: usid, activeSchoolId } = user;

  // Normalize to { where: { ... } }
  const direct = uiFilter as any;
  const where = direct?.where || {
    ...(direct?.houseId ? { houseId: direct.houseId } : {}),
    ...(direct?.academicYearId ? { academicYearId: direct.academicYearId } : {})
  };
  const houseId = where.houseId;
  const filter = { where };

  return axios
    .get(
      `${window.apiBase}/i/schools/${activeSchoolId}/leaderboards/students/${encodeURIComponent(studentId)}/awards`,
      {
        headers: { usid },
        params: {
          filter: JSON.stringify(filter),
          ...(houseId ? { houseId } : {})
        }
      }
    )
    .then(r => {
      const data = r.data as any;
      if (Array.isArray(data)) return data as AwardEvent[];
      if (Array.isArray(data?.items)) return data.items as AwardEvent[];
      if (Array.isArray(data?.results)) return data.results as AwardEvent[];
      return [];
    });
}

export function getStudentAwardsForCurrentYear(
  user: AppUser,
  studentId: string,
  uiFilter?: StudentAwardsOpts
): Promise<AwardEvent[]> {
  return getStudentAwardsForAcademicYear(user, studentId, uiFilter);
}
